
import { useRef } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { useToast } from "@/hooks/use-toast";

export interface PDFContentRef {
  current: HTMLDivElement | null;
}

interface PDFSection {
  element: HTMLElement;
  title: string;
}

export const usePDFExport = (filename: string) => {
  const { toast } = useToast();
  const pdfContentRef = useRef<HTMLDivElement>(null);

  const addPageHeader = (pdf: jsPDF, pageNumber: number, totalPages: number, showPageNumbers: boolean = true) => {
    if (showPageNumbers) {
      pdf.setFontSize(8);
      pdf.setTextColor(128);
      pdf.text(`Page ${pageNumber} of ${totalPages}`, pdf.internal.pageSize.getWidth() - 20, 10, { align: 'right' });
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 10, { align: 'left' });
    }
  };

  const addPageFooter = (pdf: jsPDF, orgName: string) => {
    pdf.setFontSize(8);
    pdf.setTextColor(128);
    const footerText = `© ${new Date().getFullYear()} AIMAA - AI Maturity Assessment and Alignment | ${orgName}`;
    pdf.text(footerText, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 10, { align: 'center' });
  };

  const getSections = async (container: HTMLDivElement): Promise<PDFSection[]> => {
    const sections: PDFSection[] = [];
    const sectionElements = container.querySelectorAll('[data-pdf-section]');
    
    for (const element of Array.from(sectionElements)) {
      const title = element.getAttribute('data-pdf-section') || '';
      sections.push({
        element: element as HTMLElement,
        title
      });
    }
    
    return sections;
  };

  const handleDownloadPDF = async () => {
    if (!pdfContentRef.current) {
      toast({
        title: "Error",
        description: "Could not find content to generate PDF.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Generating PDF",
      description: "Please wait while we generate your report...",
    });

    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const sections = await getSections(pdfContentRef.current);
      let currentPage = 1;
      const totalPages = sections.length;
      const orgName = pdfContentRef.current.getAttribute('data-organization-name') || '';

      // Process each section
      for (let i = 0; i < sections.length; i++) {
        const { element, title } = sections[i];
        
        if (i > 0) {
          pdf.addPage();
          currentPage++;
        }

        // Clear background
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');

        const isCoverPage = title === 'Cover Page';
        addPageHeader(pdf, currentPage, totalPages, !isCoverPage);
        addPageFooter(pdf, orgName);

        // Clone and prepare element for capture
        const tempElement = element.cloneNode(true) as HTMLElement;
        document.body.appendChild(tempElement);
        
        // Apply consistent styling
        Object.assign(tempElement.style, {
          width: '257mm', // A4 landscape width minus margins
          position: 'absolute',
          left: '-9999px',
          backgroundColor: '#ffffff',
          padding: '20mm',
          margin: '0',
          boxSizing: 'border-box',
          transform: 'none',
          borderRadius: '0',
          boxShadow: 'none'
        });

        // Capture with high quality settings
        const canvas = await html2canvas(tempElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: 1920,
          onclone: (doc) => {
            const el = doc.querySelector(tempElement.tagName) as HTMLElement;
            if (el) {
              // Remove any transforms or decorative styles
              el.style.transform = 'none';
              el.style.borderRadius = '0';
              el.style.boxShadow = 'none';
              // Ensure all images are loaded
              const images = el.getElementsByTagName('img');
              Array.from(images).forEach(img => {
                img.crossOrigin = 'anonymous';
              });
            }
          }
        });

        document.body.removeChild(tempElement);

        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        
        // Calculate dimensions
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20; // 20mm margins
        const contentWidth = pageWidth - (2 * margin);
        const contentHeight = (canvas.height * contentWidth) / canvas.width;
        
        // Position content
        const yPosition = isCoverPage ? margin : 30;
        pdf.addImage(imgData, 'JPEG', margin, yPosition, contentWidth, contentHeight);

        // Add section title if not cover page
        if (!isCoverPage) {
          pdf.setFontSize(16);
          pdf.setTextColor(15, 45, 105);
          pdf.text(title, margin, 20);
        }
      }

      pdf.save(filename);

      toast({
        title: "Success",
        description: "PDF report has been generated successfully",
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    pdfContentRef,
    handleDownloadPDF
  };
};
